import React, { useState, useEffect } from 'react';
import { Box, Flex, Switch, Text, Stack, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import useServerApi from 'hooks/useServerApi';
import GoogleCalendarSettings from './GoogleCalendar/GoogleCalendarSettings';
import GoogleAuthPrompt from './GoogleCalendar/GoogleAuthPrompt';
import { useGoogleCalendarSettings } from 'hooks/useGoogleCalendarSettings';

const GoogleCalendar = ({ actionsData }) => {
    const [{ post }] = useServerApi();
    const { isEnabled, isLoggedIn, settingsData, setIsEnabled, refresh } = useGoogleCalendarSettings();

    // State for calendar settings
    const [calendarSettings, setCalendarSettings] = useState({
        selectedCalendar: { id: '', name: '' },
        timeInterval: 15,
        defaultEventDuration: 30,
        customVariables: [],
        schedule: {},
        startTimeIncrements: { ':15': true, ':30': true, ':00': true },
        listOfCalendars: [],
    });

    const [isLoading] = useState(false); // Loading state for general async operations

    const [calendarSaveLoading, setCalendarSaveLoading] = useState(false);
    // Dialog state for GoogleCalendarSettings
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const toast = useToast();



    useEffect(() => {
        if (settingsData) {
            setCalendarSettings({
                selectedCalendar: { id: settingsData?.chosen_calendars?.selected_calendar_id || '', name: settingsData?.chosen_calendars?.selected_calendar_name || '' },
                timeInterval: settingsData?.chosen_calendars?.time_interval ? parseInt(settingsData.chosen_calendars.time_interval) : 15,
                defaultEventDuration: settingsData?.chosen_calendars?.default_event_duration ? parseInt(settingsData.chosen_calendars.default_event_duration) : 30,
                customVariables: settingsData?.chosen_calendars?.custom_variables || [],
                schedule: settingsData?.chosen_calendars?.schedule || {},
                startTimeIncrements: settingsData?.chosen_calendars?.start_time_increments || { ':15': true, ':30': true, ':00': true },
                listOfCalendars: settingsData?.calendars || [],
            });
        }
    }, [settingsData]);

    // --- Handlers ---
    // Removed duplicate useServerApi destructuring to avoid redeclaration error
    const handleToggleSwitch = async (event) => {
        const newEnabledState = event.target.checked;
        setIsEnabled(newEnabledState);
        if (!newEnabledState) {
            setCalendarSaveLoading(true);
            await post('/rest_endpoints/google_calendar_selection_save', {
                calendar_id: "",
                calendar_name: "",
                is_enabled: false,
                time_interval: 30,
            });
            setCalendarSaveLoading(false);
            refresh();
        }
    };

    return (
        <Box>
            <Flex alignItems='center' mb='10px'>
                <Stack direction='row' alignItems='center' flexGrow={1}>
                    <Tooltip
                        label={"Toggle Google Calendar Integration"}
                        placement="top"
                        hasArrow
                    >
                        <Switch
                            colorScheme='blue'
                            isChecked={isEnabled}
                            onChange={handleToggleSwitch}
                            isDisabled={false}
                        />
                    </Tooltip>
                    <Stack spacing='0'>
                        <Text fontSize='md'>Google Calendar Integration</Text>
                        <Text fontSize='sm' color='#6b7280' fontWeight='300'>
                            Allow the agent to access and manage your Google Calendar for scheduling.
                        </Text>
                    </Stack>
                </Stack>
            </Flex>
            {isEnabled && (
                <>
                    {isLoggedIn ? <Text mt={2} fontSize='sm' color='blue.500' cursor='pointer' onClick={() => setSettingsDialogOpen(true)}>
                        Open Settings
                    </Text> : <GoogleAuthPrompt />}
                </>
            )}
            {/* Show GoogleCalendarSettings in a modal if enabled */}
            <Modal isOpen={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent maxW="1000px" minW="420px">
                    <ModalCloseButton />
                    <ModalBody p={6} overflowY="auto" maxH="calc(100vh - 150px)">
                        <GoogleCalendarSettings
                            isEnabled={isEnabled}
                            isLoggedIn={isLoggedIn}
                            onSettingsRefresh={refresh}
                            isSaving={calendarSaveLoading}
                            setCalendarSettings={setCalendarSettings}
                            calendarSettings={calendarSettings}
                            setCalendarSaveLoading={setCalendarSaveLoading}
                            toast={toast}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default GoogleCalendar;
